/* eslint-disable prettier/prettier */
import { apiService } from './api.service';
import { ApiResponse } from '../types';

// ==============================
// Module Types
// ==============================

export interface CreateModulePayload {
	courseid: string;
	title: string;
	description: string;
	orderindex: number;
	moduletype: string;
	duration?: number;
	isrequired?: boolean;
	startdate?: Date | null;
	enddate?: Date | null;
	metadata?: any;
}

export interface UpdateModulePayload {
	title?: string;
	description?: string;
	orderindex?: number;
	moduletype?: string;
	duration?: number;
	isrequired?: boolean;
	startdate?: Date | null;
	enddate?: Date | null;
	metadata?: any;
}

export interface ActivityType {
	id: string;
	title: string;
	color: string;
	icon?: string | null;
}

export interface CreateLessonPayload {
	moduleid: string;
	title: string;
	description: string;
	orderindex: number;
	contenttype: string;
	contenturl?: string;
	contentdata?: any;
	duration?: number;
	isrequired?: boolean;
	activitytypeid?: string;
	metadata?: any;

	scheduledstarttime?: Date;
	scheduledendtime?: Date;
	timezone?: string;
}

export interface UpdateLessonPayload {
	title?: string;
	description?: string;
	orderindex?: number;
	contenttype?: string;
	contenturl?: string;
	contentdata?: any;
	duration?: number;
	isrequired?: boolean;
	activitytypeid?: string | null;
	metadata?: any;

	scheduledstarttime?: Date;
	scheduledendtime?: Date;
	timezone?: string;

	islive?: boolean;
}

export interface Lesson {
	id: string;
	moduleid: string;
	title: string;
	description: string;
	orderindex: number;
	contenttype: string;
	contenturl?: string;
	contentdata?: any;
	duration?: number;
	isrequired?: boolean;
	metadata?: any;

	scheduledstarttime?: string;
	scheduledendtime?: string;
	timezone?: string;
	islive?: boolean;
	// 🔥 NEW (from backend join)
	activitytype?: ActivityType | null;
}

export interface ResourceFile {
	id: string;
	filename: string;
	filesize: number;
	mimetype: string;
	bloburl: string;
}

export interface Resource {
	id: string;
	title: string;
	description: string;
	resourcetype: string;
	visibility: string;
	downloadcount: number;
	viewcount: number;
	createdat: string;
	uploadername: string;
	file?: ResourceFile | null;
}

export interface CourseModule {
	id: string;
	courseid: string;
	title: string;
	description: string;
	orderindex: number;
	moduletype: string;
	duration: number;
	isrequired: boolean;
	metadata?: any;

	startdate?: string | null;
	enddate?: string | null;

	lessons?: Lesson[];
	resources?: Resource[];
	moduleassessments?: any[];
}

// ==============================
// Module Service
// ==============================

class CourseModuleService {
	// ==========================
	// MODULE APIs
	// ==========================

	async createModule(data: CreateModulePayload) {
		try {
			return await apiService.post<ApiResponse<CourseModule>>(
				'/modules/create',
				data
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to create module';
		}
	}

	async getModuleById(id: string) {
		try {
			return await apiService.get<ApiResponse<CourseModule>>(
				`/modules/${id}`
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to fetch module';
		}
	}

	async getCourseModules(courseId: string) {
		try {
			return await apiService.get<ApiResponse<CourseModule[]>>(
				`/modules/courses/${courseId}/modules`
			);
		} catch (error: any) {
			throw (
				error?.response?.data?.message ||
				'Failed to fetch course modules'
			);
		}
	}

	async updateModule(id: string, data: UpdateModulePayload) {
		try {
			return await apiService.put<ApiResponse<CourseModule>>(
				`/modules/modules/${id}`, // ✅ FIXED (removed duplicate "modules")
				data
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to update module';
		}
	}

	async deleteModule(id: string) {
		try {
			return await apiService.delete<ApiResponse<void>>(
				`/modules/modules/${id}`
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to delete module';
		}
	}

	async reorderModules(
		courseId: string,
		modules: { id: string; orderindex: number }[]
	) {
		try {
			return await apiService.put<ApiResponse<void>>(
				`/modules/course/${courseId}/reorder`, // ✅ align with backend pattern
				{ modules }
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to reorder modules';
		}
	}

	// ==========================
	// LESSON APIs
	// ==========================

	// async createLesson(data: CreateLessonPayload) {
	// 	try {
	// 		return await apiService.post<ApiResponse<Lesson>>(
	// 			`/modules/lessons`,
	// 			data
	// 		);
	// 	} catch (error: any) {
	// 		throw error?.response?.data?.message || 'Failed to create lesson';
	// 	}
	// }
	async createLesson(data: CreateLessonPayload) {
  try {
    return await apiService.post<Lesson>(
      `/modules/lessons`,
      data
    );
  } catch (error: any) {
    throw error?.response?.data?.message || 'Failed to create lesson';
  }
}

	async getLessonById(id: string) {
		try {
			return await apiService.get<ApiResponse<Lesson>>(
				`/modules/lessons/${id}`
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to fetch lesson';
		}
	}

	async getModuleLessons(moduleId: string) {
		try {
			return await apiService.get<ApiResponse<Lesson[]>>(
				`/modules/${moduleId}/lessons` // ✅ FIXED path
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to fetch lessons';
		}
	}

	async updateLesson(id: string, data: UpdateLessonPayload) {
		try {
			return await apiService.put<ApiResponse<Lesson>>(
				`/modules/lessons/${id}`,
				data
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to update lesson';
		}
	}

	async deleteLesson(id: string) {
		try {
			return await apiService.delete<ApiResponse<void>>(
				`/modules/lessons/${id}`
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to delete lesson';
		}
	}

	async reorderLessons(
		moduleId: string,
		lessons: { id: string; orderindex: number }[]
	) {
		try {
			return await apiService.put<ApiResponse<void>>(
				`/modules/${moduleId}/lessons/reorder`,
				{ lessons }
			);
		} catch (error: any) {
			throw error?.response?.data?.message || 'Failed to reorder lessons';
		}
	}
}

export const courseModuleService = new CourseModuleService();
